import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Box, 
  Menu, 
  MenuItem, 
  Typography, 
  Avatar, 
  Divider, 
  ListItemIcon,
  Badge
} from "@mui/material";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import LaunchIcon from '@mui/icons-material/Launch';
import FeedbackIcon from '@mui/icons-material/Feedback'; // Import feedback icon
import logo from '../public/Kaleideo-white-logo.png';
import axios from "axios";
import { USER_URLS } from "../config/urls";
import { useEffect } from "react";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const Navbar = () => {
  const location = useLocation();
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const profileOpen = Boolean(profileAnchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  
  // Mock notification data - in a real app, this would come from the backend
  const [notifications, setNotifications] = useState([
    // { id: 1, title: "New order received", message: "Order #12345 has been placed", read: false, time: "10 min ago" },
    // { id: 2, title: "Product update", message: "New product inventory is available", read: false, time: "2 hours ago" },
    // { id: 3, title: "System notification", message: "The system will be under maintenance tonight", read: true, time: "1 day ago" }
  ]);
  
  const unreadCount = notifications.filter(notification => !notification.read).length;

  // Google Form URL for feedback
  const feedbackFormUrl = "https://forms.gle/Xng6ixPtvQ6kZwNS7";

  const [user, setUser] = useState({
    name: "",
    role: "",
    initials: ""
  });

  // Fetch user data when component mounts
  useEffect(() => {
    const fetchUserData = () => {
      const token = localStorage.getItem('token');
      
      axios.get(USER_URLS.getMe, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        const userData = response.data;

        
        // Update user state with data from API
        setUser({
          name: userData.first_name + ' '+userData.last_name,
          role: userData.role,
        });
      })
      .catch(error => {
        console.error('Error fetching user data:', error);
      });
    };
    
    fetchUserData();
  }, []);


  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };
  
  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };
  
  const markAllAsRead = () => {
    const updatedNotifications = notifications.map(notification => ({
      ...notification,
      read: true
    }));
    setNotifications(updatedNotifications);
  };
  
  const markAsRead = (id) => {
    const updatedNotifications = notifications.map(notification => 
      notification.id === id ? { ...notification, read: true } : notification
    );
    setNotifications(updatedNotifications);
  };

  const getNavLinkStyle = (path) => ({
    color: (location.pathname === path || (path === "/explore" && location.pathname === "/checkout")||(path === "/settings" && location.pathname === "/settings/user-management")) ? "white" : "#2196f3",
    textDecoration: "none",
    fontSize: "16px",
    transition: "color 0.3s ease",
    '&:hover': {
      color: (location.pathname === path || (path === "/explore" && location.pathname === "/checkout")) ? "rgba(255, 255, 255, 0.8)" : "#2196f3"
    }
  });

  const getIconButtonStyle = (path) => ({
    color: location.pathname === path ? "white" : "#2196f3",
    transition: "color 0.3s ease",
    '&:hover': {
      color: "#2196f3",
      backgroundColor: 'rgba(33, 150, 243, 0.08)'
    }
  });

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Optional: Make an API call to invalidate the token on the server
      // Only if your backend has an endpoint for this
      await axios.post(USER_URLS.logout, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear all auth-related data from localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      // Clear any other auth-related items you might have stored
      
      // Optional: Clear any app state using your state management solution
      // If using Redux:
      // dispatch(clearUserData());
      // dispatch(resetAppState());
      
      // Redirect to login page
      window.location.href = '/login';
      
      // If using React Router instead of a full page reload:
      // navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if the API call fails, still clear local data and redirect
      localStorage.clear(); // Clear everything to be safe
      window.location.href = '/login';
    }
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: "black", padding: "2px 0px" }}>
      <Toolbar 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          minHeight: "30px !important",
        }}
      >
        {/* Left: Logo & Tagline */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Link to="/explore">
            <img 
              src={logo} 
              alt="KALEIDEO" 
              style={{ height: "30px", cursor: "pointer" }} 
            />
          </Link>

          <Link 
            to="/explore" 
            style={getNavLinkStyle("/explore")}
          >
            Explore
          </Link>
          <Link 
            to="/orders" 
            style={getNavLinkStyle("/orders")}
          >
            Orders
          </Link>
          <Link 
            to="/products" 
            style={getNavLinkStyle("/products")}
          >
            Products
          </Link>

          <Link 
            to="/documentation" 
            style={getNavLinkStyle("/documentation")}
          >
            Documentation
          </Link>

          <Link 
            to="/feedback" 
            style={getNavLinkStyle("/feedback")}
            onClick={(e) => {
              e.preventDefault();
              window.open(feedbackFormUrl, '_blank');
            }}
          >
            Feedback 
            <OpenInNewIcon fontSize="1px" sx={{ml:'4px'}}/>
          </Link>
        </Box>

        {/* Right: Icons */}
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* <IconButton 
            sx={getIconButtonStyle("/documentation")}
            component={Link}
            to="/documentation"
          >
            <DescriptionIcon sx={{ fontSize: 25 }}/>
          </IconButton> */}
          <IconButton 
            sx={getIconButtonStyle("/notifications")}
            onClick={handleNotificationClick}
            aria-controls={notificationOpen ? 'notification-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={notificationOpen ? 'true' : undefined}
          >
            <Badge badgeContent={unreadCount} color="error" overlap="circular">
              {unreadCount > 0 ? (
                <NotificationsIcon sx={{ fontSize: 25 }} />
              ) : (
                <NotificationsNoneIcon sx={{ fontSize: 25 }} />
              )}
            </Badge>
          </IconButton>
          <IconButton 
            sx={getIconButtonStyle("/profile")}
            onClick={handleProfileClick}
            aria-controls={profileOpen ? 'profile-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={profileOpen ? 'true' : undefined}
          >
            <PersonOutlineIcon sx={{ fontSize: 25 }} />
          </IconButton>

          {/* Notification Dropdown Menu with Triangle Pointer */}
          <Menu
            id="notification-menu"
            anchorEl={notificationAnchorEl}
            open={notificationOpen}
            onClose={handleNotificationClose}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 1.5,
                width: 320,
                maxHeight: 400,
                borderRadius: '8px',
                zIndex: 9999,
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: -10,
                  right: 14,
                  width: 0,
                  height: 0,
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: '10px solid white',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            sx={{ zIndex: 9999 }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Typography 
                  variant="body2" 
                  sx={{ color: '#2196f3', cursor: 'pointer', fontWeight: 'medium' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    markAllAsRead();
                  }}
                >
                  Mark all as read
                </Typography>
              )}
            </Box>
            
            <Divider />
            
            <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <MenuItem 
                    key={notification.id} 
                    onClick={() => markAsRead(notification.id)}
                    sx={{ 
                      backgroundColor: notification.read ? 'transparent' : 'rgba(33, 150, 243, 0.08)',
                      px: 2,
                      py: 1.5
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {notification.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.time}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {notification.message}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No notifications
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Divider />
            
            <MenuItem component={Link} to="/notifications" onClick={handleNotificationClose} sx={{ justifyContent: 'center' }}>
              <Typography variant="body2" color="primary">
                View all notifications
              </Typography>
            </MenuItem>
          </Menu>
          
          {/* Profile Dropdown Menu with Triangle Pointer */}
          <Menu
            id="profile-menu"
            anchorEl={profileAnchorEl}
            open={profileOpen}
            onClose={handleProfileClose}
            onClick={handleProfileClose}
            PaperProps={{
              elevation: 3,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 1.5,
                width: 220,
                borderRadius: '8px',
                zIndex: 9999,
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: -10,
                  right: 14,
                  width: 0,
                  height: 0,
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: '10px solid white',
                  zIndex: 0,
                },
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            sx={{ zIndex: 9999 }}
          >
            {/* User Profile Section */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
              <Avatar 
                sx={{ 
                  bgcolor: '#999', 
                  color: 'white',
                  width: 40, 
                  height: 40,
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                {user.initials}
              </Avatar>
              <Box sx={{ ml: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {user.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.role}
                </Typography>
              </Box>
            </Box>
            
            <Divider />
            
            {/* Menu Options */}
            <MenuItem component={Link} to="/settings/profile">
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            
            <MenuItem component="a" href="https://www.kaleideo.co" target="_blank">
              <ListItemIcon>
                <LaunchIcon fontSize="small" />
              </ListItemIcon>
              Visit Website
            </MenuItem>
            
            
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;